import { createToastInstance } from './bootstrap_resolver';
import type { ResolvedInstallOptions, ToastPosition } from './types';

const TOAST_CONTAINER_ATTRIBUTE = 'data-haori-toast-container';
const TOAST_ATTRIBUTE = 'data-haori-toast';
const TOAST_LEVEL_ATTRIBUTE = 'data-haori-toast-level';
const TOAST_ACCENT_ATTRIBUTE = 'data-haori-toast-accent';
const TOAST_DISMISS_ATTRIBUTE = 'data-haori-toast-dismiss';

interface ToastAppearance {
  /** アクセント帯へ適用する Bootstrap 背景クラス。 */
  accentClassName: string;
  /** 文言非依存の識別子として付与する正規化済みレベル。 */
  level: 'success' | 'warning' | 'error' | 'info';
}

const TOAST_POSITION_CLASSES: Record<ToastPosition, string> = {
  'top-start': 'top-0 start-0',
  'top-center': 'top-0 start-50 translate-middle-x',
  'top-end': 'top-0 end-0',
  'bottom-start': 'bottom-0 start-0',
  'bottom-center': 'bottom-0 start-50 translate-middle-x',
  'bottom-end': 'bottom-0 end-0',
};

/**
 * 通知レベルを toast 表示用の見た目へ変換する。
 *
 * @param level 通知レベル。
 * @return 対応する見た目定義。
 */
function resolveToastAppearance(level?: string): ToastAppearance {
  switch (level) {
    case 'success':
      return { accentClassName: 'bg-success', level: 'success' };
    case 'warning':
      return { accentClassName: 'bg-warning', level: 'warning' };
    case 'error':
      return { accentClassName: 'bg-danger', level: 'error' };
    default:
      return { accentClassName: 'bg-info', level: 'info' };
  }
}

/**
 * toast コンテナを配置する親要素を解決する。
 *
 * @param documentObject 検索に使用する document。
 * @param options 解決済み導入設定。
 * @return 配置先の要素。
 */
function resolveToastRoot(documentObject: Document, options: ResolvedInstallOptions): HTMLElement {
  if (options.toastContainerSelector) {
    const resolvedElement = documentObject.querySelector<HTMLElement>(options.toastContainerSelector);
    if (resolvedElement) {
      return resolvedElement;
    }
  }

  return documentObject.body;
}

/**
 * toast コンテナを取得または生成する。
 *
 * @param documentObject 生成に使用する document。
 * @param options 解決済み導入設定。
 * @return 取得または生成したコンテナ。
 */
function ensureToastContainer(documentObject: Document, options: ResolvedInstallOptions): HTMLElement {
  const rootElement = resolveToastRoot(documentObject, options);
  const positionClasses = TOAST_POSITION_CLASSES[options.toastPosition ?? 'bottom-end'];
  const existingContainer = rootElement.querySelector<HTMLElement>(
    `[${TOAST_CONTAINER_ATTRIBUTE}="true"]`,
  );
  if (existingContainer) {
    const allPositionClasses = Object.values(TOAST_POSITION_CLASSES)
      .flatMap(c => c.split(' '));
    existingContainer.classList.remove(...allPositionClasses);
    existingContainer.classList.add(...positionClasses.split(' '));
    return existingContainer;
  }

  const container = documentObject.createElement('div');
  container.className = `toast-container position-fixed ${positionClasses} p-3`;
  container.setAttribute(TOAST_CONTAINER_ATTRIBUTE, 'true');
  rootElement.appendChild(container);
  return container;
}

/**
 * Bootstrap Toast を表示する。
 *
 * @param message 表示するメッセージ。
 * @param level 通知レベル。
 * @param options 解決済み導入設定。
 * @return 完了時に解決される Promise。
 */
export function showToast(
  message: string,
  level: string | undefined,
  options: ResolvedInstallOptions,
): Promise<void> {
  const documentObject = globalThis.document;
  const appearance = resolveToastAppearance(level);
  const toastElement = documentObject.createElement('div');
  toastElement.className = 'toast border bg-body text-body shadow-sm';
  toastElement.setAttribute('role', 'status');
  toastElement.setAttribute('aria-live', 'polite');
  toastElement.setAttribute('aria-atomic', 'true');
  toastElement.setAttribute(TOAST_ATTRIBUTE, 'true');
  toastElement.setAttribute(TOAST_LEVEL_ATTRIBUTE, appearance.level);

  const bodyWrapper = documentObject.createElement('div');
  bodyWrapper.className = 'd-flex align-items-stretch';

  const accentElement = documentObject.createElement('div');
  accentElement.className = `flex-shrink-0 rounded-start ${appearance.accentClassName}`;
  accentElement.style.width = '0.375rem';
  accentElement.setAttribute(TOAST_ACCENT_ATTRIBUTE, 'true');

  const bodyElement = documentObject.createElement('div');
  bodyElement.className = 'toast-body';
  bodyElement.style.whiteSpace = 'pre-line';
  bodyElement.textContent = message;

  const dismissButton = documentObject.createElement('button');
  dismissButton.type = 'button';
  dismissButton.className = 'btn-close me-2 m-auto flex-shrink-0';
  dismissButton.setAttribute('aria-label', 'Close');
  dismissButton.setAttribute(TOAST_DISMISS_ATTRIBUTE, 'true');

  bodyWrapper.appendChild(accentElement);
  bodyWrapper.appendChild(bodyElement);
  bodyWrapper.appendChild(dismissButton);
  toastElement.appendChild(bodyWrapper);
  ensureToastContainer(documentObject, options).appendChild(toastElement);

  const toastOptions = options.toastDelay !== undefined ? { delay: options.toastDelay } : undefined;
  const toastInstance = createToastInstance(toastElement, toastOptions, options.bootstrap);
  if (!toastInstance) {
    toastElement.remove();
    return Promise.reject(new Error('Bootstrap Toast is unavailable.'));
  }

  dismissButton.addEventListener('click', () => {
    toastInstance.hide?.();
  });

  toastElement.addEventListener(
    'hidden.bs.toast',
    () => {
      toastElement.remove();
      toastInstance.dispose?.();
    },
    { once: true },
  );

  try {
    toastInstance.show();
    return Promise.resolve();
  } catch (error) {
    toastElement.remove();
    toastInstance.dispose?.();
    return Promise.reject(error);
  }
}
